import { Warning } from "@mui/icons-material";
import Avatar from "@mui/material/Avatar";
import greenEmoji from "assets/images/emoji-green.png";
import orangeEmoji from "assets/images/emoji-orange.png";
import redEmoji from "assets/images/emoji-red.png";
import yellowEmoji from "assets/images/emoji-yellow.png";
import { useLanguage } from "contexts/language.context";
import moment from "moment";
import { Fragment } from "react";
import auditsService from "services/audits-service";
import useDebouncedOnChange from "utils/useDebounceOnChange";
import {
  DEV_ANSWER_TYPE_ID,
  NA_ANSWER_TYPE_ID,
  STD_ANSWER_TYPE_ID,
} from "const";
import { filteredPillars, percentage, percentageScore } from "../../utils";
import styles from "../assessmentReportStyles.module.css";

const emojiBasedOnScore = (value, { size = "60px" } = {}) => {
  if (value < 75)
    return <img src={redEmoji} style={{ width: size, height: size }} />;
  if (value < 80)
    return <img src={orangeEmoji} style={{ width: size, height: size }} />;
  if (value < 90)
    return <img src={yellowEmoji} style={{ width: size, height: size }} />;
  return <img src={greenEmoji} style={{ width: size, height: size }} />;
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const Table = ({ theads, rows }) => {
  return (
    <table style={{ tableLayout: "unset" }}>
      <thead>
        <tr>
          {theads.map((thead, index) => {
            return <th key={"Thead: " + index}>{thead}</th>;
          })}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, row_index) => {
          return (
            <tr key={"Row: " + row_index}>
              {row.map((item, item_index) => {
                return <td key={"Item: " + item_index}>{item}</td>;
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};

const CheckListTableConstruct = (pillars, audit, include) => {
  const { label } = useLanguage();
  if (!pillars) return null;

  return pillars
    .filter((p) => p.kpis.filter((k) => k.kpiValueTypeId == 2).length)
    .map((pillarData, index) => {
      const { referenceCode, description, kpis } = pillarData;
      let std_t = 0;
      let dev_t = 0;
      let na_t = 0;

      let foundEliminatoryKpi = false;
      for (const kpi of kpis) {
        const auditKpi = kpi.auditKpis[0];
        const isdev = auditKpi.kpiValueTypeAnswerId == DEV_ANSWER_TYPE_ID;
        if (isdev && auditKpi.eliminatory) {
          foundEliminatoryKpi = true;
          break;
        }
      }

      return (
        <>
          {" "}
          <div
            className={`${styles["pdf-item-container"]} printable`}
            key={`check-sub-table-${index}`}
            id="table"
          >
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <table>
                <thead>
                  <tr>
                    <th
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]}} ${styles["width-10-cell"]}`}
                    >
                      {referenceCode} {foundEliminatoryKpi ? <Warning /> : ""}
                    </th>
                    <th
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-44-cell"]}`}
                    >
                      {description}
                    </th>
                    {audit.auditTypeId != 1 && (
                      <th
                        className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                      >
                        {label("weight-singular")}
                      </th>
                    )}
                    <th
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]}${styles["helvetica-cell"]} ${styles["width-7-cell"]}`}
                    >
                      STD
                    </th>
                    <th
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-7-cell"]}`}
                    >
                      DEV
                    </th>
                    <th
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-7-cell"]}`}
                    >
                      N/A
                    </th>
                    <th
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-25-cell"]}`}
                    >
                      {label(
                        audit.auditTypeId == 1
                          ? "observation-and-action"
                          : "remark-plural",
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {kpis
                    .filter((kpi) => kpi.kpiValueTypeId == 2)
                    .map((kpiObject, row_index) => {
                      const { referenceCode, weight, auditKpis, ...rest } =
                        kpiObject;
                      const auditKpi = auditKpis[0];
                      const isstd =
                        auditKpi.kpiValueTypeAnswerId === STD_ANSWER_TYPE_ID;
                      const isdev =
                        auditKpi.kpiValueTypeAnswerId === DEV_ANSWER_TYPE_ID;
                      const isna =
                        auditKpi.kpiValueTypeAnswerId === NA_ANSWER_TYPE_ID;
                      let remark = auditKpi.answerRemark || "";
                      let description = auditKpi.description;
                      if (
                        isna &&
                        auditKpi.answerRemark &&
                        auditKpi.answerRemark !== "" &&
                        auditKpi.kpiAnswerReason &&
                        auditKpi?.kpiAnswerReason?.description !== ""
                      ) {
                        remark += " - ";
                      }
                      if (isna) {
                        remark += auditKpi?.kpiAnswerReason?.description || "";
                      }
                      if (isstd) std_t += weight;
                      if (isdev) dev_t += weight;
                      if (isna) na_t += weight;
                      return (
                        <tr
                          key={"Row: " + row_index}
                          id={"kpi-" + referenceCode}
                        >
                          <td
                            className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]}`}
                          >
                            {`${auditKpi.sequenceNumber ? auditKpi.sequenceNumber + " - " : ""} ${referenceCode}${auditKpi.eliminatory ? " - " : ""}`}
                            {auditKpi.eliminatory ? <Warning /> : ""}
                          </td>
                          <td>{description}</td>
                          {audit.auditTypeId != 1 && (
                            <td className={styles["color-2-cell"]}>{weight}</td>
                          )}
                          <td className={styles["white-cell"]}>
                            {isstd && "X"}
                          </td>
                          <td className={styles["white-cell"]}>
                            {isdev && "X"}
                          </td>
                          <td className={styles["white-cell"]}>
                            {isna && "X"}
                          </td>
                          <td className={styles["white-cell"]}>{remark}</td>
                        </tr>
                      );
                    })}
                  <tr>
                    <td
                      colSpan={2}
                      className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                    >
                      {label("total-singular")}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                    >
                      {std_t + dev_t + na_t}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                    >
                      {std_t}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                    >
                      {dev_t}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                    >
                      {na_t}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                    ></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>
          {include.actions
            ?.filter((v) => v.pillarId == pillarData.id)
            ?.map((v) => v.html)}
          <div className={styles["photo-grid"]}>
            {include.photos
              ?.filter((v) => v.pillarId == pillarData.id)
              ?.map((v) => v.html)}
          </div>
        </>
      );
    });
};

const PersonalPerceptionRatingConstruct = (pillars) => {
  const ratingPillars =
    pillars
      ?.filter((pillar) => pillar.kpis.find((kpi) => kpi.kpiValueTypeId == 3))
      .map((pillar) => ({
        ...pillar,
        kpis: pillar.kpis.filter((kpi) => kpi.kpiValueTypeId == 3),
      })) || [];

  return ratingPillars.map((pillar) => {
    return (
      <div
        className={styles[`pdf-item-container`] + " " + styles["printable"]}
        key={`check-sub-table-${pillar.id}`}
        id="table"
      >
        <div className={styles["pdf-margin"]}></div>
        <div className={styles["pdf-item"]}>
          <table>
            <thead>
              <tr>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]}} ${styles["width-10-cell"]}`}
                >
                  {pillar.referenceCode}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-44-cell"]}`}
                >
                  {pillar.description}
                </th>
                {/* {ratings.map((rating) => (
                      <th
                        key={"Rating th: " + rating}
                        className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]}${styles["helvetica-cell"]} `}
                      >
                        {rating}
                      </th>
                    ))} */}
                <th
                  colSpan={2}
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]}${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {pillar.kpis.map((kpi, row_index) => {
                return (
                  <tr key={"Row: " + row_index} id={"kpi-" + kpi.referenceCode}>
                    <td
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]}`}
                    >
                      {`${kpi.auditKpis[0].sequenceNumber ? kpi.auditKpis[0].sequenceNumber + " - " : ""} ${kpi.referenceCode}`}
                    </td>
                    <td className={styles["padded-cell"]}>
                      {kpi.auditKpis[0].description}
                    </td>
                    {/* {ratings.map((rating) => (
                          <th
                            key={"Rating td ss: " + rating}
                            className={styles["white-cell"]}
                          >
                            {rating ==
                            kpi.auditKpis[0].kpiValueTypeAnswer?.description
                              ? "X"
                              : ""}
                          </th>
                        ))} */}

                    <td className={styles["white-cell"]}>
                      {Number(kpi.auditKpis[0].kpiValueTypeAnswer?.description)}{" "}
                      / 10
                    </td>

                    <td className={styles["white-cell"]}>
                      {emojiBasedOnScore(
                        Number(
                          kpi.auditKpis[0].kpiValueTypeAnswer?.description,
                        ) * 10,
                        { size: "40px" },
                      )}
                    </td>
                  </tr>
                );
              })}
              <tr>
                <td
                  colSpan={2}
                  className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                >
                  TOTAL
                </td>
                {/* {ratings.map((rating) => (
                      <td
                        key={"Rating tf: " + rating}
                        className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                      >
                        {pillar.kpis.reduce((prev, curr) => {
                          return (
                            prev +
                            (curr.auditKpis[0].kpiValueTypeAnswer
                              ?.description == rating
                              ? rating
                              : 0)
                          );
                        }, 0)}
                      </td>
                    ))} */}

                <td
                  className={`${styles["color-3-cell"]} ${styles["bold-10-cell"]} ${styles["calibri-cell"]}`}
                  colSpan={2}
                >
                  {pillar.kpis.reduce((prev, curr) => {
                    return (
                      prev +
                      Number(curr.auditKpis[0].kpiValueTypeAnswer?.description)
                    );
                  }, 0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className={styles["pdf-margin"]}></div>
      </div>
    );
  });
};

const MysteryVisitNarrativeConstruct = (audit) => {
  return (
    <div
      className={styles[`pdf-item-container`] + " " + styles["printable"]}
      key={`narrative-table`}
      id="table"
    >
      <div className={styles["pdf-margin"]}></div>
      <div className={styles["pdf-item"]}></div>
      <div className={styles["narrative-text"]}>{audit?.narrativeText}</div>
      <div className={styles["pdf-margin"]}></div>
    </div>
  );
};

const ShopperCommentsAndObservationsConstruct = (pillars) => {
  const questionPillars =
    pillars
      ?.filter((pillar) => pillar.kpis.find((kpi) => kpi.kpiValueTypeId == 4))
      .map((pillar) => ({
        ...pillar,
        kpis: pillar.kpis.filter((kpi) => kpi.kpiValueTypeId == 4),
      })) || [];

  return questionPillars.map((pillar) => {
    return (
      <div
        className={styles[`pdf-item-container`] + " " + styles["printable"]}
        key={`check-sub-table-${pillar.id}`}
        id="table"
      >
        <div className={styles["pdf-margin"]}></div>
        <div className={styles["pdf-item"]}>
          <table>
            <thead>
              <tr>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]}} ${styles["width-10-cell"]}`}
                >
                  {pillar.referenceCode}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-44-cell"]}`}
                  colSpan={2}
                >
                  {pillar.description}
                </th>
              </tr>

              <tr>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                >
                  REF
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-44-cell"]}`}
                >
                  Question
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]} ${styles["width-44-cell"]}`}
                >
                  Answer
                </th>
              </tr>
            </thead>
            <tbody>
              {pillar.kpis.map((kpi, row_index) => {
                return (
                  <tr key={"Row: " + row_index} id={"kpi-" + kpi.referenceCode}>
                    <td
                      className={`${styles["color-1-cell"]} ${styles["bold-10-cell"]} ${styles["helvetica-cell"]}`}
                    >
                      {`${kpi.auditKpis[0].sequenceNumber ? kpi.auditKpis[0].sequenceNumber + " - " : ""} ${kpi.referenceCode}`}
                    </td>
                    <td className={styles["padded-cell"]}>
                      {kpi.auditKpis[0].description}
                    </td>
                    <td className={styles["padded-cell"]}>
                      {kpi.auditKpis[0].answerValue}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className={styles["pdf-margin"]}></div>
      </div>
    );
  });
};

const GeneralitiesConstruct = (audit) => {
  const receipt = audit?.auditReceipt;
  if (!receipt) return;
  const rows = [
    {
      label: "Number",
      value: receipt?.number,
    },
    {
      label: "Total",
      value: receipt?.total,
    },
    {
      label: "Tips",
      value: receipt?.tips,
    },
    {
      label: "Total Paid",
      value: receipt?.totalPaid,
    },
    {
      label: "Shopper Age",
      value: receipt?.shopperAge,
    },
    {
      label: "Shopper Gender",
      value: receipt?.shopperGender,
    },
    {
      label: "Number of People",
      value: receipt?.numberOfPeople,
    },
    {
      label: "Manager Name",
      value: receipt?.managerName,
    },
    {
      label: "Waiter/Operator Name",
      value: receipt?.waiterName,
    },
    // {
    //   label: "Ordered Items",
    //   value: receipt?.orderedItems,
    // },
  ];

  return (
    <div
      className={`${styles["pdf-item-container"]} ${styles["printable"]}`}
      id="table"
    >
      <div className={styles["pdf-margin"]}></div>
      <div className={styles["pdf-item"]}>
        <table>
          <thead>
            <tr>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-5-cell"]}`}
                colSpan={2}
              >
                General Information
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
              >
                Branch
              </td>
              <td
                className={`${styles["color-3-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
              >
                {audit.branch.name}
              </td>
            </tr>
            {rows.map((row, index) => {
              return (
                <Fragment key={"Generalities row: " + row.label}>
                  {/* {index == rows.length - 1 ? (
                    <tr>
                      <td
                        colSpan={2}
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                        style={{ paddingTop: "10px" }}
                      >
                        {"   "}
                      </td>
                    </tr>
                  ) : (
                    ""
                  )} */}
                  <tr>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                    >
                      {row.label}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                    >
                      {(typeof row.value == "string"
                        ? row.value.split("\n").map((v) => (
                            <Fragment key={"v: " + v}>
                              {v}
                              <br />
                            </Fragment>
                          ))
                        : row.value) ?? "None"}
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </table>
        <img
          style={{
            height: "500px",
            objectFit: "contain",
            marginTop: "12px",
          }}
          src={receipt.attachment}
        />
      </div>
      <div className={styles["pdf-margin"]}></div>
    </div>
  );
};

const ActionPlanTableConstructArray = (pillars, devStat, deviationCounts) => {
  const { label } = useLanguage();
  if (!pillars) return null;

  return filteredPillars(pillars, DEV_ANSWER_TYPE_ID).map(
    (pillarData, index) => {
      const { referenceCode, description, kpis } = pillarData;
      return {
        html: (
          <div
            className={`${styles["pdf-item-container"]} printable`}
            key={`plan-sub-table-${index}`}
            id="table"
          >
            <div className={styles["pdf-margin"]}></div>
            <div className={styles["pdf-item"]}>
              <table>
                <thead>
                  <tr>
                    <th
                      className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                    >
                      {referenceCode}
                    </th>
                    <th
                      colSpan={2}
                      className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["width-65-cell"]}`}
                    >
                      {description}
                    </th>
                    <th
                      colSpan={3}
                      className={`${styles["color-1-cell"]} ${styles["helvetica-cell"]} ${styles["width-30-cell"]}`}
                    >
                      {label("frequency-of-deviation")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                    >
                      {label("ref-singular")}
                    </td>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                      style={{ width: "50%" }}
                    >
                      {label("deviation-singular")}
                    </td>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                      style={{ width: "50%" }}
                    >
                      {label("corrective-action")}
                    </td>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                    >
                      1
                    </td>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                    >
                      2
                    </td>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["helvetica-cell"]} ${styles["bold-12-cell"]}`}
                    >
                      3+
                    </td>
                  </tr>
                  {kpis.map((kpiObject, row_index) => {
                    const { referenceCode, auditKpis } = kpiObject;
                    const devUsed = devStat.reduce((acc, id) => {
                      if (id === auditKpis[0]?.kpiDeviationId) {
                        return acc + 1;
                      } else {
                        return acc;
                      }
                    }, 0);

                    let deviationDescription = "";
                    let actionDescription = "";
                    let remarks = "";
                    if (auditKpis) {
                      deviationDescription =
                        auditKpis[0].kpiDeviation?.description;
                      actionDescription = auditKpis[0].kpiAction?.description;
                      remarks = auditKpis[0].answerRemark;
                    }
                    return (
                      <tr key={"Row: " + row_index}>
                        <td
                          className={`${styles["color-3-cell"]} ${styles["helvetica-cell"]} ${styles["bold-10-cell"]}`}
                        >
                          {`${auditKpis[0].sequenceNumber ? `${auditKpis[0].sequenceNumber} - ` : ""}${referenceCode}`}
                        </td>
                        <td>
                          {deviationDescription?.trim().toLowerCase() ===
                          "other"
                            ? remarks
                            : deviationDescription}
                        </td>
                        <td>{actionDescription}</td>
                        <td className={styles["white-cell"]}>
                          {(!deviationCounts[kpiObject.id] ||
                            deviationCounts[kpiObject.id] === 1) &&
                            "X"}
                        </td>
                        <td className={styles["white-cell"]}>
                          {deviationCounts[kpiObject.id] === 2 && "X"}
                        </td>
                        <td className={styles["white-cell"]}>
                          {deviationCounts[kpiObject.id] >= 3 && "X"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <div className={styles["pdf-margin"]}></div>
          </div>
        ),
        pillarId: pillarData.id,
      };
    },
  );
};

const ActionPlanTableConstruct = (pillars, devStat, deviationCounts) => {
  return ActionPlanTableConstructArray(pillars, devStat, deviationCounts)?.map(
    (v) => v.html,
  );
};

const DashboardTableConstruct = (pillars, audit, groupBy) => {
  const { label } = useLanguage();
  if (!pillars)
    return {
      dashboardTableHtml: null,
      score: 0,
    };

  const ratingPillars =
    pillars
      ?.filter((pillar) => pillar.kpis.find((kpi) => kpi.kpiValueTypeId == 3))
      .map((pillar) => ({
        ...pillar,
        kpis: pillar.kpis.filter((kpi) => kpi.kpiValueTypeId == 3),
      })) || [];

  const ratings =
    ratingPillars?.[0]?.kpis[0].kpiValueType.kpiValueTypeAnswers.map((v) =>
      Number(v.description),
    ) || [];

  let std_gt = 0;
  let dev_gt = 0;
  let na_gt = 0;

  let eff_std = 0;
  return {
    dashboardTableHtml: (
      <div className={`${styles["pdf-item-container"]} printable`} id="table">
        <div className={styles["pdf-margin"]}></div>
        <div className={styles["pdf-item"]}>
          {audit.auditTypeId == 2 ? (
            <table>
              <thead>
                <tr>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-5-cell"]}`}
                    colSpan={5}
                  >
                    Personal Perception Rating (PPR)
                  </th>{" "}
                </tr>
                <tr>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-5-cell"]}`}
                  >
                    Ref
                  </th>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-45-cell"]}`}
                  >
                    Pillar
                  </th>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                  >
                    Weight
                  </th>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-15-cell"]}`}
                  >
                    Score
                  </th>

                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-15-cell"]}`}
                  >
                    Score %
                  </th>
                </tr>
              </thead>
              <tbody>
                {ratingPillars.map((pillar) => {
                  return (
                    <tr key={"Row pillar dash: " + pillar.id}>
                      <td
                        className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                      >
                        {pillar.referenceCode}
                      </td>
                      <td
                        className={`${styles["color-3-cell"]} ${styles["left-align-cell"]}`}
                      >
                        {pillar.description}
                      </td>
                      <td className={styles["color-2-cell"]}>
                        {pillar.kpis.length * ratings[ratings.length - 1]}
                      </td>
                      <td className={styles["color-3-cell"]}>
                        {pillar.kpis.reduce((prev, curr) => {
                          return (
                            prev +
                            Number(
                              curr?.auditKpis[0].kpiValueTypeAnswer
                                .description || "0",
                            )
                          );
                        }, 0)}
                      </td>

                      <td
                        className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                      >
                        {percentage(
                          pillar.kpis.reduce((prev, curr) => {
                            return (
                              prev +
                              Number(
                                curr?.auditKpis[0].kpiValueTypeAnswer
                                  .description || "0",
                              )
                            );
                          }, 0),
                          ratings[ratings.length - 1],
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className={styles["color-1-cell"]}></td>
                  <td className={styles["color-1-cell"]}>Total</td>
                  <td className={styles["color-1-cell"]}>
                    {ratingPillars.reduce((prev, curr) => {
                      return (
                        prev + curr.kpis.length * ratings[ratings.length - 1]
                      );
                    }, 0)}
                  </td>
                  <td
                    className={`${styles["color-2-cell"]}  ${styles["helvetica-cell"]}`}
                  >
                    {ratingPillars.reduce((prev, curr) => {
                      return (
                        prev +
                        curr.kpis.reduce((prev, curr) => {
                          return (
                            prev +
                            Number(
                              curr?.auditKpis[0].kpiValueTypeAnswer
                                .description || "0",
                            )
                          );
                        }, 0)
                      );
                    }, 0)}
                  </td>
                  <td
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  >
                    {percentage(
                      ratingPillars.reduce((prev, curr) => {
                        return (
                          prev +
                          curr.kpis.reduce((prev, curr) => {
                            return (
                              prev +
                              Number(
                                curr?.auditKpis[0].kpiValueTypeAnswer
                                  .description || "0",
                              )
                            );
                          }, 0)
                        );
                      }, 0),
                      ratingPillars.reduce((prev, curr) => {
                        return (
                          prev + curr.kpis.length * ratings[ratings.length - 1]
                        );
                      }, 0),
                    )}
                  </td>
                </tr>
              </tfoot>
            </table>
          ) : null}

          <table>
            <thead>
              {audit.auditTypeId == 2 ? (
                <tr>
                  <th
                    colSpan={7}
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                  >
                    Object Evaluation Score (OES)
                  </th>
                </tr>
              ) : null}
              <tr>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                >
                  {label("ref-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-45-cell"]}`}
                >
                  {groupBy == 1
                    ? label("pillar-singular")
                    : label("competency-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
                >
                  {label("weight-singular")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-15-cell"]}`}
                >
                  {label("standard-point-plural")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-15-cell"]}`}
                >
                  {label("deviation-point-plural")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-15-cell"]}`}
                >
                  {label("na-point-plural")}
                </th>
                <th
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-15-cell"]}`}
                >
                  {label("score-singular")} %
                </th>
              </tr>
            </thead>
            <tbody>
              {pillars.map((pillarObject, row_index) => {
                const { description, referenceCode, kpis } = pillarObject;
                let std_t = 0;
                let dev_t = 0;
                let na_t = 0;

                let foundEliminatoryKpi = false;
                for (let index = 0; index < kpis.length; index++) {
                  const element = kpis[index];
                  const auditKpi = element.auditKpis[0];
                  const weight = auditKpi.weight ?? element.weight;
                  const isstd =
                    auditKpi.kpiValueTypeAnswerId === STD_ANSWER_TYPE_ID;
                  const isdev =
                    auditKpi.kpiValueTypeAnswerId === DEV_ANSWER_TYPE_ID;
                  const isna =
                    auditKpi.kpiValueTypeAnswerId === NA_ANSWER_TYPE_ID;
                  if (isdev && auditKpi.eliminatory) {
                    foundEliminatoryKpi = true;
                  }

                  if (isstd) std_t += weight;
                  if (isdev) dev_t += weight;
                  if (isna) na_t += weight;
                }

                if (!foundEliminatoryKpi) eff_std += std_t;

                std_gt += std_t;
                dev_gt += dev_t;
                na_gt += na_t;

                return (
                  <tr key={"Row: " + row_index}>
                    <td
                      className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                    >
                      {referenceCode}
                    </td>
                    <td
                      className={`${styles["color-3-cell"]} ${styles["start-align-cell"]}`}
                    >
                      {description}
                    </td>
                    <td className={styles["color-2-cell"]}>
                      {std_t + dev_t + na_t}
                    </td>
                    <td className={styles["color-3-cell"]}>{std_t}</td>
                    <td className={styles["color-3-cell"]}>{dev_t}</td>
                    <td className={styles["color-3-cell"]}>{na_t}</td>
                    <td
                      className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                    >
                      {!foundEliminatoryKpi
                        ? std_t + dev_t == 0
                          ? "—"
                          : percentage(std_t, std_t + dev_t)
                        : percentage(0, 100)}
                      {foundEliminatoryKpi ? (
                        <>
                          {" "}
                          <Warning />
                        </>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td rowSpan={2} className={styles["color-1-cell"]}></td>
                <td className={styles["color-3-cell"]}>
                  {label("total-singular")}
                </td>
                <td className={styles["color-2-cell"]}>
                  {std_gt + dev_gt + na_gt}
                </td>
                <td className={styles["color-3-cell"]}>{std_gt}</td>
                <td className={styles["color-3-cell"]}>{dev_gt}</td>
                <td className={styles["color-3-cell"]}>{na_gt}</td>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  rowSpan={2}
                >
                  {std_gt === 0 ? "-" : percentage(eff_std, std_gt + dev_gt)}
                </td>
              </tr>
              <tr>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  {label("total-singular")} %
                </td>
                <td
                  className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  100 %
                </td>
                <td
                  className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  {percentage(std_gt, std_gt + dev_gt + na_gt)}
                </td>
                <td
                  className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  {percentage(dev_gt, std_gt + dev_gt + na_gt)}
                </td>
                <td
                  className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                >
                  {percentage(na_gt, std_gt + dev_gt + na_gt)}
                </td>
              </tr>
            </tfoot>
          </table>

          {audit.auditTypeId == 2 ? (
            <table>
              <thead>
                <tr>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-5-cell"]}`}
                  >
                    Overall Evaluation Index (OEI)
                  </th>
                  <th
                    className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-5-cell"]}`}
                  >
                    {percentage(
                      (Number(
                        percentageScore(
                          ratingPillars.reduce((prev, curr) => {
                            return (
                              prev +
                              curr.kpis.reduce((prev, curr) => {
                                return (
                                  prev +
                                  Number(
                                    curr?.auditKpis[0].kpiValueTypeAnswer
                                      .description || "0",
                                  )
                                );
                              }, 0)
                            );
                          }, 0),
                          ratingPillars.reduce((prev, curr) => {
                            return (
                              prev +
                              curr.kpis.length * ratings[ratings.length - 1]
                            );
                          }, 0),
                        ),
                      ) +
                        Number(percentageScore(std_gt, std_gt + dev_gt))) /
                        2,
                      100,
                    )}
                  </th>
                </tr>
              </thead>
            </table>
          ) : null}
        </div>
        <div className={styles["pdf-margin"]}></div>
      </div>
    ),
    score:
      audit.auditTypeId == 2
        ? (
            (Number(
              percentageScore(
                ratingPillars.reduce((prev, curr) => {
                  return (
                    prev +
                    curr.kpis.reduce((prev, curr) => {
                      return (
                        prev +
                        Number(
                          curr?.auditKpis[0].kpiValueTypeAnswer.description ||
                            "0",
                        )
                      );
                    }, 0)
                  );
                }, 0),
                ratingPillars.reduce((prev, curr) => {
                  return prev + curr.kpis.length * ratings[ratings.length - 1];
                }, 0),
              ),
            ) +
              Number(percentageScore(std_gt, std_gt + dev_gt))) /
            2
          ).toFixed(2)
        : std_gt === 0
          ? "-"
          : percentageScore(eff_std, std_gt + dev_gt),
  };
};

const countReportedPhotos = (pillars, typeId) => {
  if (!pillars) return 0;
  return filteredPillars(pillars, typeId).reduce((total, pillarObject) => {
    return (
      total +
      pillarObject.kpis.reduce((kpiTotal, kpiObject) => {
        return kpiTotal + (kpiObject.auditKpis?.[0]?.attachments?.length || 0);
      }, 0)
    );
  }, 0);
};

function ReportedPhotosContructArray(pillars, typeId, label) {
  if (!pillars || countReportedPhotos(pillars, typeId) === 0) return null;

  return filteredPillars(pillars, typeId).map((pillarObject) => ({
    pillarId: pillarObject.id,
    html: pillarObject.kpis.map((kpiObject, kpiIndex) => {
      const { referenceCode, auditKpis } = kpiObject;
      const auditKpi = auditKpis[0];
      const riskDescription = auditKpi.kpiRisk?.description || "";
      const deviationDescription = auditKpi.kpiDeviation?.description || "";
      const attachments = auditKpi.attachments || [];
      if (!attachments.length) return null;
      return (
        <div className={styles["photo-kpi-group"]} key={`kpi-${referenceCode}-${kpiIndex}`}>
          <div className={styles["photo-kpi-info"]}>
            <a href={"#kpi-" + referenceCode}>{`${label(typeId == DEV_ANSWER_TYPE_ID ? "deviation-reference" : "kpi-reference")}: ${referenceCode}`}</a>
            {typeId == DEV_ANSWER_TYPE_ID ? null : <span>{auditKpi.description}</span>}
            {typeId == DEV_ANSWER_TYPE_ID && deviationDescription?.trim().toUpperCase() !== "OTHER" ? <span>{deviationDescription}</span> : null}
            {typeId == DEV_ANSWER_TYPE_ID && riskDescription ? <span style={{ color: "red" }}>{riskDescription}</span> : null}
            {auditKpi.answerRemark ? <span style={{ color: "#41B06E" }}>{auditKpi.answerRemark}</span> : null}
          </div>
          <div className={styles["photo-kpi-attachments"]}>
            {attachments.map((attachmentObject, attachmentIndex) => (
              <div className={`${styles["pdf-item-container"]} ${styles["photo-card"]} printable`} key={`attachment-${attachmentObject.id || attachmentIndex}`}>
                <div className={styles["pdf-item"]}>
                  <div className={styles["photo-container"]}>
                    <Avatar sx={{ width: "200px", height: "200px", cursor: "pointer", position: "relative", boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px", "& img": { height: "100%", objectFit: "cover", objectPosition: "center" } }} variant="rounded" src={attachmentObject.url}></Avatar>
                    {attachmentObject.remarks ? <div className={styles["photo-info"]}><span style={{ color: "#00bbd4" }}>{attachmentObject.remarks}</span></div> : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }),
  }));
}
function ReportedPhotosContruct(pillars, typeId, label) {
  return (
    <div className={styles["photo-grid"]}>
      {ReportedPhotosContructArray(pillars, typeId, label)?.map((v) => v.html)}
    </div>
  );
}

function ReportedTasksPhotosContruct(tasks, label) {
  if (!tasks) return null;

  return (
    <div className={styles["photo-grid"]}>
      {tasks
        .filter((task) =>
          (task.inputAttachments?.length
            ? task.inputAttachments
            : task.auditKpi?.attachments
          )?.length,
        )
        .map((task) => {
        const attachments = task.inputAttachments?.length
          ? task.inputAttachments
          : task.auditKpi.attachments;

        return (
          <div className={styles["photo-kpi-group"]} key={`task-${task.id}`}>
            <div className={styles["photo-kpi-info"]}>
              <span style={{ color: "#1976d2" }}>
                {`${label("reference-code-singular")}: ${task.referenceCode || task.auditKpi.kpi.referenceCode}`}
              </span>
              <span>{task.auditKpi.kpiDeviation?.description || ""}</span>
              {task.auditKpi?.answerRemark ? (
                <span style={{ color: "#41B06E" }}>
                  {task.auditKpi.answerRemark}
                </span>
              ) : null}
              {task?.remarks ? <span>{task.remarks}</span> : null}
              <span style={{ color: "red" }}>
                {label("created-on")} {moment(new Date(task.createdAt)).format(
                  "MMM Do YYYY, h:mm a",
                )}
              </span>
            </div>
            <div className={styles["photo-kpi-attachments"]}>
              {attachments.map((attachment) => (
                <div
                  className={`${styles["pdf-item-container"]} ${styles["photo-card"]} printable`}
                  key={`attachment-${attachment.id}`}
                >
                  <div className={styles["pdf-item"]}>
                    <div className={styles["photo-container"]}>
                      <Avatar
                        sx={{
                          width: "200px",
                          height: "200px",
                          cursor: "pointer",
                          position: "relative",
                          boxShadow: "rgba(0, 0, 0, 0.24) 0px 3px 8px",
                          "& img": {
                            height: "100%",
                            objectFit: "cover",
                            objectPosition: "center",
                          },
                        }}
                        variant="rounded"
                        src={attachment.url}
                      />
                      {attachment?.remarks ? (
                        <div className={styles["photo-info"]}>
                          <span style={{ color: "#00bbd4" }}>
                            {attachment.remarks}
                          </span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
const TasksConstruct = (tasks) => {
  const { label } = useLanguage();

  const lateTasks = tasks.filter(
    (task) => new Date(task.dueDate) <= new Date(),
  );

  const comingTasks = tasks.filter(
    (task) => new Date(task.dueDate) > new Date(),
  );

  return (
    <div className={`${styles["pdf-item-container"]} printable`} id="table">
      <div className={styles["pdf-margin"]}></div>
      <div className={styles["pdf-item"]}>
        <table>
          <thead>
            <tr>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-5-cell"]}`}
              >
                {label("ref-singular")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-50-cell"]}`}
              >
                {label("title-singular")} — {label("description-singular")} —{" "}
                {label("detail-plural")}
              </th>

              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
              >
                {label("status-singular")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-20-cell"]}`}
              >
                {label("remark-plural")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
              >
                {label("duedate-singular")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
              >
                {label("priority-singular")}
              </th>
              <th
                className={`${styles["color-1-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["width-10-cell"]}`}
              >
                {label("assigned-to-singular")}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td
                colSpan={7}
                className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
              >
                {label("late-tasks")} ({lateTasks.length})
              </td>
            </tr>
            {lateTasks.map((task) => (
              <>
                <tr key={`title-${task.id}`}>
                  <td
                    rowSpan={3}
                    className={`${styles["color-3-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  >
                    {task.referenceCode || task.auditKpi.kpi.referenceCode}
                  </td>
                  <td
                    className={`${styles["color-3-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["start-align-cell"]}`}
                  >
                    {task.title || "None"}
                  </td>
                  <td className={styles["color-3-cell"]} rowSpan={3}>
                    {task.status?.name}
                  </td>
                  <td className={styles["color-3-cell"]} rowSpan={3}>
                    {task.remarks?.trim() || "—"}
                  </td>
                  <td className={styles["color-3-cell"]} rowSpan={3}>
                    {moment(
                      new Date(task.dueDate).toISOString().replace("z", ""),
                    ).format("MMM Do YYYY, h:mm a")}
                    {/* {moment.utc(task.dueDate).local().format("MMM Do YYYY, h:mm a")} */}
                  </td>
                  <td
                    rowSpan={3}
                    className={`${
                      task.priority.id == 1
                        ? styles["color-3-cell"]
                        : task.priority.id == 2
                          ? styles["color-2-cell"]
                          : styles["color-1-cell"]
                    } ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  >
                    {task.priority?.name}
                  </td>
                  <td
                    rowSpan={3}
                    className={styles["color-1-cell"]}
                    style={{ wordBreak: "break-all" }}
                  >
                    {task.assignedTo?.name}
                  </td>
                </tr>

                <tr key={`description-${task.id}`}>
                  <td
                    className={`${styles["color-3-cell"]} ${styles["helvetica-cell"]} ${styles["start-align-cell"]}`}
                  >
                    {task?.actionToTake || "None"}
                  </td>
                </tr>

                <tr key={`details-${task.id}`}>
                  <td
                    className={`${styles["color-3-cell"]} ${styles["helvetica-cell"]} ${styles["start-align-cell"]}`}
                  >
                    {task?.actionDetails || "None"}
                  </td>
                </tr>
              </>
            ))}
            <tr>
              <td
                className={`${styles["color-2-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                colSpan={7}
              >
                {label("coming-tasks")} ({comingTasks.length})
              </td>
            </tr>
            {comingTasks.map((task) => (
              <>
                {/* Row 1 - Title */}
                <tr key={`title-${task.id}`}>
                  <td
                    rowSpan={3}
                    className={`${styles["color-3-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  >
                    {task.referenceCode || task?.auditKpi?.kpi?.referenceCode}
                  </td>
                  <td
                    className={`${styles["color-3-cell"]} ${styles["bold-12-cell"]} ${styles["helvetica-cell"]} ${styles["start-align-cell"]}`}
                  >
                    {task.title || "None"}
                  </td>
                  <td className={styles["color-3-cell"]} rowSpan={3}>
                    {task.status?.name}
                  </td>
                  <td className={styles["color-3-cell"]} rowSpan={3}>
                    {task.remarks?.trim() || "—"}
                  </td>
                  <td className={styles["color-3-cell"]} rowSpan={3}>
                    {moment(
                      new Date(task.dueDate).toISOString().replace("z", ""),
                    ).format("MMM Do YYYY, h:mm a")}
                    {/* {moment.utc(task.dueDate).local().format("MMM Do YYYY, h:mm a")} */}
                  </td>
                  <td
                    rowSpan={3}
                    className={`${
                      task.priority.id == 1
                        ? styles["color-3-cell"]
                        : task.priority.id == 2
                          ? styles["color-2-cell"]
                          : styles["color-1-cell"]
                    } ${styles["bold-12-cell"]} ${styles["helvetica-cell"]}`}
                  >
                    {task.priority?.name}
                  </td>
                  <td
                    rowSpan={3}
                    className={styles["color-1-cell"]}
                    style={{ wordBreak: "break-all" }}
                  >
                    {task.assignedTo?.name}
                  </td>
                </tr>

                {/* Row 2 - Description */}
                <tr key={`description-${task.id}`}>
                  <td
                    className={`${styles["color-3-cell"]} ${styles["helvetica-cell"]} ${styles["start-align-cell"]}`}
                  >
                    {task?.actionToTake || "None"}
                  </td>
                </tr>

                {/* Row 3 - Details */}
                <tr key={`details-${task.id}`}>
                  <td
                    className={`${styles["color-3-cell"]} ${styles["helvetica-cell"]} ${styles["start-align-cell"]}`}
                  >
                    {task?.actionDetails || "None"}
                  </td>
                </tr>
              </>
            ))}
          </tbody>
        </table>
      </div>
      <div className={styles["pdf-margin"]}></div>
    </div>
  );
};

function AttachmentParams({
  auditId,
  attachmentId,
  initialValue,
  getReportAttachments,
}) {
  const { value, setValue, handleChange } = useDebouncedOnChange(
    initialValue,
    500,
  );

  return (
    <MDInput
      sx={{ mt: 2 }}
      label="Extra Remarks"
      value={value || ""}
      onChange={handleChange(async (value) => {
        await auditsService.updateReportAttachment(auditId, attachmentId, {
          remarks: value,
        });

        getReportAttachments();
      })}
      fullWidth
      multiline
      minRows={2}
    />
  );
}

export default {
  Table,
  ActionPlanTableConstruct,
  ActionPlanTableConstructArray,
  CheckListTableConstruct,
  PersonalPerceptionRatingConstruct,
  MysteryVisitNarrativeConstruct,
  ShopperCommentsAndObservationsConstruct,
  GeneralitiesConstruct,
  DashboardTableConstruct,
  ReportedPhotosContruct,
  ReportedPhotosContructArray,
  countReportedPhotos,
  ReportedTasksPhotosContruct,
  TasksConstruct,
  AttachmentParams,
};
